import { window } from '@mtsbank/ui-kit'
import { getPreviousRoute } from '@shared/lib/next'
import { translit } from '../translit'
import {
  actionGroups,
  amountSources,
  entryPoints,
  eventActions,
  eventCategories,
  events,
  paymentMethods,
} from './constants'
import { GtmEventPayload, sendGtmEvent } from './sendGtmEvent'
import { AmountSource, EntryPoint, FunnelContent, PaymentMethod } from './types'

const ENTRY_STORAGE_KEY = 'gtm_redesign_entry_point'

const isEntryPoint = (value: string): value is EntryPoint => Object.values(entryPoints).includes(value as EntryPoint)

export const toCd1 = (name: string) => translit(name)

export const mapPaymentMethod = (method: string): PaymentMethod => {
  if (method === paymentMethods.sbp || method === 'SBP') {
    return paymentMethods.sbp
  }

  return paymentMethods.mtsAccount
}

export const setEntryPoint = (entry: EntryPoint) => {
  try {
    window.sessionStorage?.setItem(ENTRY_STORAGE_KEY, entry)
  } catch {
    // sessionStorage недоступен (SSR / private mode)
  }
}

export const getEntryPoint = (entry?: EntryPoint): EntryPoint => {
  if (entry) {
    return entry
  }

  try {
    const stored = window.sessionStorage?.getItem(ENTRY_STORAGE_KEY)

    if (typeof stored === 'string' && isEntryPoint(stored)) {
      return stored
    }
  } catch {
    // sessionStorage недоступен
  }

  return entryPoints.direct
}

export const sendPageviewGtm = (fields: GtmEventPayload) =>
  sendGtmEvent({
    event: events.mtsPageview,
    eventCategory: eventCategories.igrovayaStranica,
    eventAction: eventActions.screenShow,
    actionGroup: actionGroups.nonInteractions,
    ref: getPreviousRoute(),
    ...fields,
  })

export const sendInteractionGtm = (fields: GtmEventPayload) =>
  sendGtmEvent({
    event: events.mtsSpaCyber,
    eventCategory: eventCategories.igrovayaStranica,
    actionGroup: actionGroups.interactions,
    ...fields,
  })

export type PaymentContextParams = {
  content?: FunnelContent
  productName: string
  entry?: EntryPoint
  amount?: string
  requestId?: string
  transactionId?: string
  paymentMethod?: string
  amountSource?: AmountSource
  denomination?: string
  errorName?: string
}

export const buildPaymentContext = ({
  content,
  productName,
  entry,
  amount,
  requestId,
  transactionId,
  paymentMethod,
  amountSource,
  denomination,
  errorName,
}: PaymentContextParams): GtmEventPayload => ({
  ...(content ? { eventContent: content } : {}),
  CD1: {
    serviceName: toCd1(productName),
    entryPoint: getEntryPoint(entry),
    ...(denomination ? { tariff: toCd1(denomination) } : {}),
    ...(paymentMethod ? { bindingType: mapPaymentMethod(paymentMethod) } : {}),
    ...(amountSource ? { purchaseType: amountSource } : {}),
    ...(errorName ? { errorName: toCd1(errorName) } : {}),
  },
  ...(amount ? { eventValue: amount } : {}),
  ...(requestId ? { filterName: requestId } : {}),
  ...(transactionId ? { intId: transactionId } : {}),
})

export const defaultAmountSource = (amountSource?: AmountSource): AmountSource =>
  amountSource ?? amountSources.rekvizity
